import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { EmptyState, ErrorState, LoadingState } from '../components/StatePanel';
import { appConfig } from '../config/appConfig';
import { useSiteConfig } from '../context/SiteConfigContext';
import { useAsyncData } from '../hooks/useAsyncData';
import type {
  AdminLetter,
  AdminMemory,
  AdminPhoto,
  IssuedInvite,
  ModerationQueue,
} from '../models/Moderation';
import type { SiteConfig } from '../models/SiteConfig';
import type { LetterFormErrors, LetterFormValues } from '../utils/letterValidation';
import { isValidLetterForm, toNewLetter, validateLetterForm } from '../utils/letterValidation';
import type { MemoryFormErrors, MemoryFormValues } from '../utils/memoryValidation';
import { adminService } from '../services/adminService';
import { toUserMessage } from '../utils/errors';
import { acceptAttribute, acceptedFormatsLabel, validateSelectedFiles } from '../utils/fileValidation';
import { formatDateTime, toCalendarDate } from '../utils/formatDate';
import { isValidMemoryForm, validateMemoryForm } from '../utils/memoryValidation';
import { isEmbeddableYouTubeUrl } from '../utils/youtube';
import styles from './AdminPage.module.css';

type Busy = { id: string } | null;

/**
 * Everything an administrator does, in the order they tend to need it: hand out
 * the family invitation, deal with what has come in, correct the memorial's
 * details, and look after the memories and photographs already on the site.
 *
 * Cloudflare Access guards this path at the edge, and the Worker checks every
 * request behind it. Neither depends on this component.
 */
export function AdminPage() {
  return (
    <div className="container">
      <header className="page-header">
        <h1>Looking after this memorial</h1>
        <p>
          Nothing family and friends submit appears on the site until you approve it here.
        </p>
      </header>

      <div className={styles.sections}>
        <InvitationSection />
        <ModerationSection />
        <MemorialDetailsSection />
        <MainPhotographSection />
        <MemoriesOnSiteSection />
        <LettersOnSiteSection />
        <PhotographsOnSiteSection />
      </div>
    </div>
  );
}

function ModerationSection() {
  const { data, status, error, reload } = useAsyncData<ModerationQueue>(
    () => adminService.getQueue(),
    'The queue could not be loaded.',
  );
  const [busy, setBusy] = useState<Busy>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const act = useCallback(
    async (id: string, action: () => Promise<void>) => {
      setBusy({ id });
      setFailure(null);
      try {
        await action();
        reload();
      } catch (cause) {
        setFailure(toUserMessage(cause, 'That could not be saved. Please try again.'));
      } finally {
        setBusy(null);
      }
    },
    [reload],
  );

  // Each list is read defensively because this is a reply from another program:
  // the API Worker deploys separately from this page, so a queue from a Worker
  // that predates one of these kinds arrives without that key. Reaching straight
  // for .map on it took the whole section down, which is worse than showing one
  // kind of waiting item fewer.
  const memories = data?.memories ?? [];
  const letters = data?.letters ?? [];
  const photos = data?.photos ?? [];
  const waiting = memories.length + letters.length + photos.length;

  return (
    <section className={styles.section} aria-labelledby="queue-heading">
      <div className={styles.sectionHeader}>
        <h2 id="queue-heading">Waiting for you</h2>
        {status === 'success' ? (
          <span className={styles.count}>
            {waiting} {waiting === 1 ? 'item' : 'items'}
          </span>
        ) : null}
      </div>

      {failure ? (
        <div className="notice notice--error" role="alert">
          <p>{failure}</p>
        </div>
      ) : null}

      {status === 'loading' ? <LoadingState label="Loading the queue…" /> : null}
      {status === 'error' ? (
        <ErrorState message={error ?? 'The queue could not be loaded.'} onRetry={reload} />
      ) : null}

      {status === 'success' && data !== null ? (
        waiting === 0 ? (
          <EmptyState title="Nothing is waiting">
            <p>Everything shared so far has been dealt with.</p>
          </EmptyState>
        ) : (
          <>
            {memories.map((memory) => (
              <article key={memory.id} className={`card ${styles.queueItem}`}>
                <div className={styles.queueMeta}>
                  <h3 className={styles.queueName}>{memory.name}</h3>
                  <time className={styles.queueDate} dateTime={memory.created}>
                    {formatDateTime(memory.created)}
                  </time>
                </div>
                <p className={styles.queueMessage}>{memory.message}</p>
                <div className={styles.actions}>
                  <button
                    type="button"
                    className="button"
                    disabled={busy?.id === memory.id}
                    onClick={() => act(memory.id, () => adminService.approveMemory(memory.id))}
                  >
                    Publish this memory
                  </button>
                  <button
                    type="button"
                    className="button button--quiet"
                    disabled={busy?.id === memory.id}
                    onClick={() => act(memory.id, () => adminService.removeMemory(memory.id))}
                  >
                    Delete permanently
                  </button>
                </div>
              </article>
            ))}

            {letters.map((letter) => (
              <article key={letter.id} className={`card ${styles.queueItem}`}>
                <div className={styles.queueMeta}>
                  <h3 className={styles.queueName}>{letter.name}</h3>
                  <time className={styles.queueDate} dateTime={letter.created}>
                    {formatDateTime(letter.created)}
                  </time>
                </div>
                {/* Labelled, because a waiting letter and a waiting memory look
                    identical otherwise and are published by different buttons. */}
                <p className={styles.queueMessage}>{letter.message}</p>
                <div className={styles.actions}>
                  <button
                    type="button"
                    className="button"
                    disabled={busy?.id === letter.id}
                    onClick={() => act(letter.id, () => adminService.approveLetter(letter.id))}
                  >
                    Publish this letter
                  </button>
                  <button
                    type="button"
                    className="button button--quiet"
                    disabled={busy?.id === letter.id}
                    onClick={() => act(letter.id, () => adminService.removeLetter(letter.id))}
                  >
                    Delete permanently
                  </button>
                </div>
              </article>
            ))}

            {photos.length > 0 ? (
              <ul className={styles.photoGrid}>
                {photos.map((photo) => (
                  <li key={photo.id} className={`card ${styles.queueItem}`}>
                    <img
                      className={styles.queuePreview}
                      src={photo.thumbnailUrl}
                      alt={photo.caption ?? 'A photograph waiting to be approved'}
                      loading="lazy"
                    />
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className="button"
                        disabled={busy?.id === photo.id}
                        onClick={() => act(photo.id, () => adminService.approvePhoto(photo.id))}
                      >
                        Publish
                      </button>
                      <button
                        type="button"
                        className="button button--quiet"
                        disabled={busy?.id === photo.id}
                        onClick={() => act(photo.id, () => adminService.removePhoto(photo.id))}
                      >
                        Delete permanently
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )
      ) : null}
    </section>
  );
}

/**
 * Letters that are already on the site.
 *
 * Published only: anything still waiting appears in the moderation queue above,
 * and listing it under a heading that says it is on the site would say
 * something untrue about it. The same reasoning as the memories below.
 *
 * There is no form here. Letters are written on the public page like memories
 * are, by anybody holding the family invitation, and they wait here to be
 * approved -- so this section corrects and removes rather than composes.
 */
function LettersOnSiteSection() {
  const { data, status, error, reload } = useAsyncData<AdminLetter[]>(
    async () => (await adminService.listAllLetters()).filter((letter) => letter.status === 'published'),
    'The letters could not be loaded.',
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<LetterFormValues>({ name: '', message: '' });
  const [errors, setErrors] = useState<LetterFormErrors>({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  function startEditing(letter: AdminLetter) {
    setEditingId(letter.id);
    setDraft({ name: letter.name, message: letter.message });
    setErrors({});
    setFailure(null);
  }

  async function save(letter: AdminLetter) {
    const validationErrors = validateLetterForm(draft);
    setErrors(validationErrors);
    if (!isValidLetterForm(validationErrors)) return;

    setSaving(true);
    setFailure(null);
    try {
      await adminService.updateLetter(letter.id, toNewLetter(draft));
      setEditingId(null);
      reload();
    } catch (cause) {
      setFailure(toUserMessage(cause, 'That letter could not be saved. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete(letter: AdminLetter) {
    setDeleting(true);
    setFailure(null);
    try {
      await adminService.removeLetter(letter.id);
      setConfirmingDeleteId(null);
      reload();
    } catch (cause) {
      setFailure(toUserMessage(cause, 'That letter could not be removed. Please try again.'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section className={styles.section} aria-labelledby="letters-site-heading">
      <div className={styles.sectionHeader}>
        <h2 id="letters-site-heading">Letters on the site</h2>
        {status === 'success' && data !== null ? (
          <span className={styles.count}>
            {data.length} {data.length === 1 ? 'letter' : 'letters'}
          </span>
        ) : null}
      </div>

      {failure ? (
        <div className="notice notice--error" role="alert">
          <p>{failure}</p>
        </div>
      ) : null}

      {status === 'loading' ? <LoadingState label="Loading the letters…" /> : null}
      {status === 'error' ? (
        <ErrorState message={error ?? 'The letters could not be loaded.'} onRetry={reload} />
      ) : null}

      {status === 'success' && data !== null ? (
        data.length === 0 ? (
          <EmptyState title="No letters yet">
            <p>Once a letter is published it will appear here for editing or removal.</p>
          </EmptyState>
        ) : (
          <>
            {data.map((letter) => (
              <article key={letter.id} className={`card ${styles.queueItem}`}>
                {editingId === letter.id ? (
                  <>
                    <div className="field">
                      <label className="field__label" htmlFor={`edit-letter-name-${letter.id}`}>
                        Name
                      </label>
                      <input
                        id={`edit-letter-name-${letter.id}`}
                        type="text"
                        className="field__control"
                        value={draft.name}
                        onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                        disabled={saving}
                      />
                      {errors.name ? <span className="field__error">{errors.name}</span> : null}
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor={`edit-letter-message-${letter.id}`}>
                        Letter
                      </label>
                      <textarea
                        id={`edit-letter-message-${letter.id}`}
                        className="field__control"
                        rows={12}
                        value={draft.message}
                        onChange={(event) => setDraft({ ...draft, message: event.target.value })}
                        disabled={saving}
                      />
                      {errors.message ? (
                        <span className="field__error">{errors.message}</span>
                      ) : null}
                    </div>
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className="button"
                        disabled={saving}
                        onClick={() => void save(letter)}
                      >
                        {saving ? 'Saving…' : 'Save changes'}
                      </button>
                      <button
                        type="button"
                        className="button button--quiet"
                        disabled={saving}
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.queueMeta}>
                      <h3 className={styles.queueName}>{letter.name}</h3>
                      <time className={styles.queueDate} dateTime={letter.created}>
                        {formatDateTime(letter.created)}
                      </time>
                    </div>
                    <p className={styles.queueMessage}>{letter.message}</p>
                    <div className={styles.actions}>
                      <button type="button" className="button" onClick={() => startEditing(letter)}>
                        Edit this letter
                      </button>
                      <button
                        type="button"
                        className="button button--quiet"
                        disabled={confirmingDeleteId === letter.id || deleting}
                        onClick={() => setConfirmingDeleteId(letter.id)}
                      >
                        Delete this letter
                      </button>
                    </div>
                    {confirmingDeleteId === letter.id ? (
                      <div className="notice" role="status">
                        <p>Deleting a letter cannot be undone.</p>
                        <div className={styles.actions}>
                          <button
                            type="button"
                            className="button"
                            disabled={deleting}
                            onClick={() => void confirmDelete(letter)}
                          >
                            {deleting ? 'Deleting…' : 'Yes, delete it'}
                          </button>
                          <button
                            type="button"
                            className="button button--quiet"
                            disabled={deleting}
                            onClick={() => setConfirmingDeleteId(null)}
                          >
                            Keep it
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </article>
            ))}
          </>
        )
      ) : null}
    </section>
  );
}

function MemoriesOnSiteSection() {
  // The endpoint returns everything the administrator looks after, waiting
  // included. Only the published ones belong here: what is still waiting is
  // already shown in the queue above, and listing it under a heading that says
  // it is on the site would say something untrue about it.
  const { data, status, error, reload } = useAsyncData<AdminMemory[]>(
    async () =>
      (await adminService.listAllMemories()).filter((memory) => memory.status === 'published'),
    'The memories could not be loaded.',
  );
  // The memory currently being edited, if any. Holding the id rather than the
  // whole object keeps the form in step with whatever was last loaded.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<MemoryFormValues>({ name: '', message: '' });
  const [errors, setErrors] = useState<MemoryFormErrors>({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // The memory awaiting the second "yes, delete it" click. A single id is
  // enough because only one confirmation is shown at a time.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  function startEditing(memory: AdminMemory) {
    setEditingId(memory.id);
    setDraft({ name: memory.name, message: memory.message });
    setErrors({});
    setFailure(null);
  }

  function cancelEditing() {
    setEditingId(null);
    setErrors({});
    setFailure(null);
  }

  async function save(memory: AdminMemory) {
    const validationErrors = validateMemoryForm(draft);
    setErrors(validationErrors);
    if (!isValidMemoryForm(validationErrors)) return;

    setSaving(true);
    setFailure(null);
    try {
      await adminService.updateMemory(memory.id, { name: draft.name.trim(), message: draft.message.trim() });
      setEditingId(null);
      reload();
    } catch (cause) {
      setFailure(toUserMessage(cause, 'That memory could not be saved. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete(memory: AdminMemory) {
    setDeleting(true);
    setFailure(null);
    try {
      await adminService.removeMemory(memory.id);
      setConfirmingDeleteId(null);
      reload();
    } catch (cause) {
      setFailure(toUserMessage(cause, 'That memory could not be removed. Please try again.'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section className={styles.section} aria-labelledby="memories-site-heading">
      <div className={styles.sectionHeader}>
        <h2 id="memories-site-heading">Memories on the site</h2>
        {status === 'success' && data !== null ? (
          <span className={styles.count}>
            {data.length} {data.length === 1 ? 'memory' : 'memories'}
          </span>
        ) : null}
      </div>

      {failure ? (
        <div className="notice notice--error" role="alert">
          <p>{failure}</p>
        </div>
      ) : null}

      {status === 'loading' ? <LoadingState label="Loading the memories…" /> : null}
      {status === 'error' ? (
        <ErrorState message={error ?? 'The memories could not be loaded.'} onRetry={reload} />
      ) : null}

      {status === 'success' && data !== null ? (
        data.length === 0 ? (
          <EmptyState title="No memories yet">
            <p>Once a memory is published it will appear here for editing or removal.</p>
          </EmptyState>
        ) : (
          <>
            {data.map((memory) => (
              <article key={memory.id} className={`card ${styles.queueItem}`}>
                {editingId === memory.id ? (
                  <>
                    <div className="field">
                      <label className="field__label" htmlFor={`edit-memory-name-${memory.id}`}>
                        Contributor's name
                      </label>
                      <input
                        id={`edit-memory-name-${memory.id}`}
                        type="text"
                        className="field__control"
                        value={draft.name}
                        onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                        disabled={saving}
                      />
                      {errors.name ? <span className="field__error">{errors.name}</span> : null}
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor={`edit-memory-message-${memory.id}`}>
                        Memory
                      </label>
                      <textarea
                        id={`edit-memory-message-${memory.id}`}
                        className="field__control"
                        rows={6}
                        value={draft.message}
                        onChange={(event) => setDraft({ ...draft, message: event.target.value })}
                        disabled={saving}
                      />
                      {errors.message ? <span className="field__error">{errors.message}</span> : null}
                    </div>
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className="button"
                        disabled={saving}
                        onClick={() => void save(memory)}
                      >
                        {saving ? 'Saving…' : 'Save changes'}
                      </button>
                      <button
                        type="button"
                        className="button button--quiet"
                        disabled={saving}
                        onClick={cancelEditing}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.queueMeta}>
                      <h3 className={styles.queueName}>{memory.name}</h3>
                      <time className={styles.queueDate} dateTime={memory.created}>
                        {formatDateTime(memory.created)}
                      </time>
                    </div>
                    <p className={styles.queueMessage}>{memory.message}</p>
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className="button"
                        onClick={() => startEditing(memory)}
                      >
                        Edit this memory
                      </button>
                      <button
                        type="button"
                        className="button button--quiet"
                        disabled={confirmingDeleteId === memory.id || deleting}
                        onClick={() => setConfirmingDeleteId(memory.id)}
                      >
                        Delete this memory
                      </button>
                    </div>
                    {confirmingDeleteId === memory.id ? (
                      <div className="notice" role="status">
                        <p>Deleting a memory cannot be undone.</p>
                        <div className={styles.actions}>
                          <button
                            type="button"
                            className="button"
                            disabled={deleting}
                            onClick={() => void confirmDelete(memory)}
                          >
                            {deleting ? 'Deleting…' : 'Yes, delete it'}
                          </button>
                          <button
                            type="button"
                            className="button button--quiet"
                            disabled={deleting}
                            onClick={() => setConfirmingDeleteId(null)}
                          >
                            Keep it
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </article>
            ))}
          </>
        )
      ) : null}
    </section>
  );
}

function PhotographsOnSiteSection() {
  // Published only, for the same reason as the memories above.
  const { data, status, error, reload } = useAsyncData<AdminPhoto[]>(
    async () =>
      (await adminService.listAllPhotos()).filter((photo) => photo.status === 'published'),
    'The photographs could not be loaded.',
  );
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function confirmDelete(photo: AdminPhoto) {
    setDeleting(true);
    setFailure(null);
    try {
      await adminService.removePhoto(photo.id);
      setConfirmingDeleteId(null);
      reload();
    } catch (cause) {
      setFailure(toUserMessage(cause, 'That photograph could not be removed. Please try again.'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section className={styles.section} aria-labelledby="photos-site-heading">
      <div className={styles.sectionHeader}>
        <h2 id="photos-site-heading">Photographs on the site</h2>
        {status === 'success' && data !== null ? (
          <span className={styles.count}>
            {data.length} {data.length === 1 ? 'photograph' : 'photographs'}
          </span>
        ) : null}
      </div>

      {failure ? (
        <div className="notice notice--error" role="alert">
          <p>{failure}</p>
        </div>
      ) : null}

      {status === 'loading' ? <LoadingState label="Loading the photographs…" /> : null}
      {status === 'error' ? (
        <ErrorState message={error ?? 'The photographs could not be loaded.'} onRetry={reload} />
      ) : null}

      {status === 'success' && data !== null ? (
        data.length === 0 ? (
          <EmptyState title="No photographs">
            <p>Once a photograph is published it will appear here for removal.</p>
          </EmptyState>
        ) : (
          <ul className={styles.photoGrid}>
            {data.map((photo) => (
              <li key={photo.id} className={`card ${styles.queueItem}`}>
                <img
                  className={styles.queuePreview}
                  src={photo.thumbnailUrl}
                  alt={photo.caption ?? 'A photograph on the site'}
                  loading="lazy"
                />
                <div className={styles.actions}>
                  <button
                    type="button"
                    className="button button--quiet"
                    disabled={confirmingDeleteId === photo.id || deleting}
                    onClick={() => setConfirmingDeleteId(photo.id)}
                  >
                    Delete this photograph
                  </button>
                </div>
                {confirmingDeleteId === photo.id ? (
                  <div className="notice" role="status">
                    <p>Deleting a photograph cannot be undone.</p>
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className="button"
                        disabled={deleting}
                        onClick={() => void confirmDelete(photo)}
                      >
                        {deleting ? 'Deleting…' : 'Yes, delete it'}
                      </button>
                      <button
                        type="button"
                        className="button button--quiet"
                        disabled={deleting}
                        onClick={() => setConfirmingDeleteId(null)}
                      >
                        Keep it
                      </button>
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}

const FIELDS: {
  name: keyof SiteConfig;
  label: string;
  hint?: string;
  multiline?: boolean;
  date?: boolean;
}[] = [
  { name: 'title', label: 'Heading', hint: 'Shown above the name, for example "In Loving Memory".' },
  { name: 'name', label: 'Name' },
  {
    name: 'dateOfBirth',
    label: 'Date of birth',
    hint: 'Leave blank if you would rather not say.',
    date: true,
  },
  {
    name: 'dateOfDeath',
    label: 'Date of death',
    hint: 'Leave blank if you would rather not say.',
    date: true,
  },
  { name: 'welcomeText', label: 'Introduction', multiline: true },
  {
    name: 'youtubeUrl',
    label: 'Video',
    hint: 'Paste a YouTube link to show a video on the home page, or leave it blank for none. It begins playing without sound when a visitor reaches it.',
  },
];

/** The date fields as a date picker can show them. See toCalendarDate. */
const DATE_FIELDS = FIELDS.filter((field) => field.date === true).map((field) => field.name);

function MemorialDetailsSection() {
  const { siteConfig, status, reload } = useSiteConfig();
  const [draft, setDraft] = useState<SiteConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // A date the picker cannot show would otherwise sit blank and be saved back
  // unchanged, so anything written the old way is rewritten before it is shown.
  const loaded = useMemo(() => {
    if (siteConfig === null) return null;
    const rewritten = { ...siteConfig };
    for (const field of DATE_FIELDS) rewritten[field] = toCalendarDate(siteConfig[field]);
    return rewritten;
  }, [siteConfig]);

  const values = draft ?? loaded;

  async function save() {
    if (values === null) return;

    // Said here rather than left to the Worker's refusal, so the message names
    // the field and arrives without a round trip. The Worker checks it too:
    // this is a courtesy, not the guard.
    if (!isEmbeddableYouTubeUrl(values.youtubeUrl)) {
      setSaved(false);
      setFailure(
        'That video link was not one this site can read. Copy the address from YouTube — it should look like https://www.youtube.com/watch?v=… or https://youtu.be/… — or leave the field blank for no video.',
      );
      return;
    }

    setSaving(true);
    setFailure(null);
    setSaved(false);
    try {
      await adminService.saveSiteConfig(values);
      setSaved(true);
      reload();
    } catch (cause) {
      setFailure(toUserMessage(cause, 'Those details could not be saved. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={styles.section} aria-labelledby="details-heading">
      <div className={styles.sectionHeader}>
        <h2 id="details-heading">Memorial details</h2>
      </div>

      {status === 'loading' || values === null ? (
        <LoadingState label="Loading the details…" />
      ) : (
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          {saved ? (
            <div className="notice notice--success notice--spaced" role="status">
              <p>Saved. The site now shows these details.</p>
            </div>
          ) : null}

          {failure ? (
            <div className="notice notice--error notice--spaced" role="alert">
              <p>{failure}</p>
            </div>
          ) : null}

          {FIELDS.map((field) => (
            <div className="field" key={field.name}>
              <label className="field__label" htmlFor={`config-${field.name}`}>
                {field.label}
              </label>
              {field.hint ? (
                <span className="field__hint" id={`config-${field.name}-hint`}>
                  {field.hint}
                </span>
              ) : null}
              {field.multiline === true ? (
                <textarea
                  id={`config-${field.name}`}
                  className="field__control"
                  rows={4}
                  value={values[field.name] ?? ''}
                  aria-describedby={field.hint ? `config-${field.name}-hint` : undefined}
                  onChange={(event) => setDraft({ ...values, [field.name]: event.target.value })}
                  disabled={saving}
                />
              ) : (
                <input
                  id={`config-${field.name}`}
                  type={field.date === true ? 'date' : 'text'}
                  className="field__control"
                  value={values[field.name] ?? ''}
                  aria-describedby={field.hint ? `config-${field.name}-hint` : undefined}
                  onChange={(event) => setDraft({ ...values, [field.name]: event.target.value })}
                  disabled={saving}
                />
              )}
            </div>
          ))}

          <button type="submit" className="button" disabled={saving}>
            {saving ? 'Saving…' : 'Save details'}
          </button>
        </form>
      )}
    </section>
  );
}

/**
 * The photograph at the top of the home page.
 *
 * Its own section rather than a field in the form above, because uploading
 * takes effect the moment it succeeds, whereas the details beside it only apply
 * when Save is pressed. Putting an immediate action inside a form that has a
 * Save button invites somebody to press Save expecting it to do something.
 */
function MainPhotographSection() {
  const { siteConfig, status, reload } = useSiteConfig();

  const [chosen, setChosen] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [rejection, setRejection] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // What the Worker last told us is on the site. Null means we have not changed
  // anything this visit, so the loaded configuration is the truth. Held locally
  // because /api/config is cached for half a minute: reloading alone could show
  // the previous photograph back for that long, which looks like a failed save.
  const [current, setCurrent] = useState<string | null>(null);

  // The object URL behind the preview, mirrored into a ref so it can be released
  // without reading it back through state. Releasing has to happen outside the
  // state updater: React may call an updater more than once, so an updater that
  // does anything beyond returning the next value is not safe to write.
  const previewRef = useRef<string | null>(null);
  previewRef.current = previewUrl;

  const replacePreview = useCallback((next: string | null) => {
    if (previewRef.current !== null) URL.revokeObjectURL(previewRef.current);
    previewRef.current = next;
    setPreviewUrl(next);
  }, []);

  // Whatever is still held when the page goes away.
  useEffect(
    () => () => {
      if (previewRef.current !== null) URL.revokeObjectURL(previewRef.current);
    },
    [],
  );

  const handleFileChosen = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Allow the same file to be chosen again after it has been cleared.
      event.target.value = '';

      setFailure(null);
      if (file === undefined) return;

      const { accepted, rejected } = validateSelectedFiles([file]);
      const [first] = accepted;
      if (first === undefined) {
        setRejection(rejected[0]?.reason ?? 'That photograph could not be used.');
        setChosen(null);
        replacePreview(null);
        return;
      }

      setRejection(null);
      setChosen(first);
      replacePreview(URL.createObjectURL(first));
    },
    [replacePreview],
  );

  const discard = useCallback(() => {
    setChosen(null);
    replacePreview(null);
    setRejection(null);
  }, [replacePreview]);

  async function upload() {
    if (chosen === null) return;

    setWorking(true);
    setFailure(null);
    try {
      const { mainPhoto } = await adminService.uploadMainPhoto(chosen);
      setCurrent(mainPhoto);
      discard();
      reload();
    } catch (cause) {
      setFailure(toUserMessage(cause, 'That photograph could not be uploaded. Please try again.'));
    } finally {
      setWorking(false);
    }
  }

  async function remove() {
    setWorking(true);
    setFailure(null);
    try {
      await adminService.removeMainPhoto();
      setCurrent('');
      setConfirmingRemove(false);
      reload();
    } catch (cause) {
      setFailure(toUserMessage(cause, 'That photograph could not be removed. Please try again.'));
    } finally {
      setWorking(false);
    }
  }

  const inPlace = current ?? siteConfig?.mainPhoto ?? '';

  return (
    <section className={styles.section} aria-labelledby="main-photo-heading">
      <div className={styles.sectionHeader}>
        <h2 id="main-photo-heading">The main photograph</h2>
      </div>

      <div className="card">
        <p>
          The photograph shown at the top of the home page. Choose one from this device — it is
          resized here before it is sent, so it uploads quickly even on a slow connection, and that
          also removes the hidden information cameras record in a photo file, such as where it was
          taken.
        </p>
        <p>{acceptedFormatsLabel} photographs can be used.</p>

        {failure ? (
          <div className="notice notice--error notice--spaced" role="alert">
            <p>{failure}</p>
          </div>
        ) : null}

        {rejection ? (
          <div className="notice notice--error notice--spaced" role="alert">
            <p>{rejection}</p>
          </div>
        ) : null}

        {status === 'loading' && current === null ? (
          <LoadingState label="Loading the photograph…" />
        ) : (
          <figure className={styles.mainPhotoFigure}>
            {previewUrl !== null ? (
              <>
                <img className={styles.mainPhotoPreview} src={previewUrl} alt="The photograph you have chosen" />
                <figcaption>Not uploaded yet.</figcaption>
              </>
            ) : inPlace ? (
              <img className={styles.mainPhotoPreview} src={inPlace} alt="The main photograph as it appears on the home page" />
            ) : (
              <figcaption>No photograph has been uploaded yet.</figcaption>
            )}
          </figure>
        )}

        <div className="field">
          <label className="field__label" htmlFor="main-photo-file">
            {inPlace ? 'Choose a different photograph' : 'Choose a photograph'}
          </label>
          <input
            id="main-photo-file"
            type="file"
            className="field__control"
            accept={acceptAttribute}
            onChange={handleFileChosen}
            disabled={working}
          />
        </div>

        <div className={styles.actions}>
          {chosen !== null ? (
            <>
              <button type="button" className="button" disabled={working} onClick={() => void upload()}>
                {working ? 'Uploading…' : 'Use this photograph'}
              </button>
              <button type="button" className="button button--quiet" disabled={working} onClick={discard}>
                Cancel
              </button>
            </>
          ) : null}

          {chosen === null && inPlace !== '' && !confirmingRemove ? (
            <button
              type="button"
              className="button button--quiet"
              disabled={working}
              onClick={() => setConfirmingRemove(true)}
            >
              Remove the photograph
            </button>
          ) : null}
        </div>

        {confirmingRemove ? (
          <div className="notice" role="status">
            <p>The home page will show no photograph until another is uploaded.</p>
            <div className={styles.actions}>
              <button type="button" className="button" disabled={working} onClick={() => void remove()}>
                {working ? 'Removing…' : 'Yes, remove it'}
              </button>
              <button
                type="button"
                className="button button--quiet"
                disabled={working}
                onClick={() => setConfirmingRemove(false)}
              >
                Keep it
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function InvitationSection() {
  const [issued, setIssued] = useState<IssuedInvite | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function rotate() {
    setWorking(true);
    setFailure(null);
    try {
      setIssued(await adminService.rotateInvite());
      setConfirming(false);
    } catch (cause) {
      setFailure(toUserMessage(cause, 'A new invitation could not be created.'));
    } finally {
      setWorking(false);
    }
  }

  const link = issued === null ? null : `${window.location.origin}${appConfig.baseUrl}invite/${issued.token}`;

  return (
    <section className={styles.section} aria-labelledby="invite-heading">
      <div className={styles.sectionHeader}>
        <h2 id="invite-heading">The family invitation</h2>
      </div>

      <div className="card">
        <p>
          Anyone with the invitation link can add memories and photographs, which then wait here for
          you. The link <strong>does not expire on its own</strong> — once it has been sent it keeps
          working, forwarded or saved, until you replace it here. Creating a new one{' '}
          <strong>signs out everybody using the old link</strong> — which is what to do if it has
          been passed around more widely than you meant.
        </p>

        {failure ? (
          <div className="notice notice--error notice--spaced" role="alert">
            <p>{failure}</p>
          </div>
        ) : null}

        {link !== null ? (
          <div className="notice notice--success" role="status">
            <p className="notice__heading">Here is the new invitation link</p>
            <p>Copy it now — it cannot be shown again.</p>
            <code className={styles.inviteToken}>{link}</code>
          </div>
        ) : null}

        {confirming ? (
          <div className={`notice ${styles.rotateAction}`} role="status">
            <p>
              Everybody using the current link will be signed out. They will each need the new link
              before they can add a memory or a photograph again.
            </p>
            <div className={styles.actions}>
              <button
                type="button"
                className="button"
                disabled={working}
                onClick={() => void rotate()}
              >
                {working ? 'Creating…' : 'Yes, create a new link'}
              </button>
              <button
                type="button"
                className="button button--quiet"
                disabled={working}
                onClick={() => setConfirming(false)}
              >
                Keep the current link
              </button>
            </div>
          </div>
        ) : (
          <p className={styles.rotateAction}>
            <button
              type="button"
              className="button button--secondary"
              onClick={() => setConfirming(true)}
            >
              Create a new invitation link
            </button>
          </p>
        )}
      </div>
    </section>
  );
}
